import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enTranslation from './locales/en.json';
import itTranslation from './locales/it.json';

export const SUPPORTED_LANGUAGES = ['en', 'it'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Where i18next-browser-languagedetector caches the resolved language. */
export const LANGUAGE_STORAGE_KEY = 'i18nextLng';
const DETECTION_MIGRATION_KEY = 'i18nDetectionEnabled';

/**
 * The app used to force `lng: 'it'`, which skipped detection entirely and made
 * the detector cache 'it' for every visitor. Clear that one cached value so the
 * browser language gets detected again; a cached 'en' is left alone, because
 * with an Italian default it could only come from an explicit choice.
 */
function enableDetectionForReturningVisitors(): void {
  try {
    if (localStorage.getItem(DETECTION_MIGRATION_KEY)) return;
    localStorage.setItem(DETECTION_MIGRATION_KEY, 'done');

    if (localStorage.getItem(LANGUAGE_STORAGE_KEY) === 'it') {
      localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (private mode): detection falls back to the navigator.
  }
}

/**
 * Tell the browser which language the page is actually in. Kept in sync with
 * i18next so Chrome/Safari never see English markup filled with Italian copy —
 * that mismatch is what triggers the "translate this page?" behaviour.
 */
function syncDocumentLanguage(language: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language?.split('-')[0] || 'en';
}

enableDetectionForReturningVisitors();

i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    resources: {
      en: {
        translation: enTranslation
      },
      it: {
        translation: itTranslation
      }
    },
    // No `lng` here on purpose: setting it bypasses the detector, which is why
    // everybody used to land on Italian regardless of their browser.
    // English is the fallback so a missing key never shows Italian to someone
    // reading the app in another language.
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // 'it-IT' resolves to 'it': the app compares i18n.language with 'it'/'en'.
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    debug: import.meta.env.DEV,

    // Common namespaces across all translations
    ns: ['translation'],
    defaultNS: 'translation',

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // 'it-IT' from the browser becomes plain 'it'.
      convertDetectedLanguage: (language: string) => language.split('-')[0],
    },
  });

syncDocumentLanguage(i18n.language);
i18n.on('languageChanged', syncDocumentLanguage);

export default i18n;
